<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useContactProfileIdentityLock } from 'dashboard/composables/useContactProfileIdentityLock';
import ContactInfoRow from './ContactInfoRow.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SocialIcons from './SocialIcons.vue';
import EditContact from './EditContact.vue';
import ContactMergeModal from 'dashboard/modules/contact/ContactMergeModal.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import NextButton from 'dashboard/components-next/button/Button.vue';
import VoiceCallButton from 'dashboard/components-next/Contacts/VoiceCallButton.vue';

import {
  isAConversationRoute,
  isAInboxViewRoute,
  getConversationDashboardRoute,
} from '../../../../helper/routeHelpers';
import { emitter } from 'shared/helpers/mitt';

export default {
  components: {
    NextButton,
    ContactInfoRow,
    EditContact,
    Avatar,
    ComposeConversation,
    SocialIcons,
    ContactMergeModal,
    VoiceCallButton,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
    showScheduleAction: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['panelClose', 'openSchedule'],
  setup() {
    const { isAdmin } = useAdmin();
    const { isContactIdentityLockedForCurrentUser, canEditContactProfile, canDeleteContactProfile } =
      useContactProfileIdentityLock();
    return {
      isAdmin,
      isContactIdentityLockedForCurrentUser,
      canEditContactProfile,
      canDeleteContactProfile,
    };
  },
  data() {
    return {
      showEditModal: false,
      showMergeModal: false,
      showDeleteModal: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'contacts/getUIFlags',
      canUseNativeBrowserPhone: 'getCurrentAccountHasBrowserPhoneAccess',
    }),
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    location() {
      const {
        country = '',
        city = '',
        country_code: countryCode,
      } = this.additionalAttributes;
      const cityAndCountry = [city, country].filter(item => !!item).join(', ');

      if (!cityAndCountry) {
        return '';
      }
      return this.findCountryFlag(countryCode, cityAndCountry);
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
        social_telegram_user_name: telegramUsername,
      } = this.additionalAttributes;
      return {
        twitter: twitterScreenName,
        telegram: telegramUsername,
        ...(socialProfiles || {}),
      };
    },
    whatsappUsername() {
      const attrs = this.additionalAttributes;
      return attrs.whatsapp_username || attrs.whatsappUsername || '';
    },
    whatsappBsuid() {
      const attrs = this.additionalAttributes;
      return attrs.whatsapp_bsuid || attrs.whatsappBsuid || '';
    },
    // Delete Modal
    confirmDeleteMessage() {
      return ` ${this.contact.name}?`;
    },
  },
  watch: {
    'contact.id': {
      handler(id) {
        this.$store.dispatch('contacts/fetchContactableInbox', id);
      },
      immediate: true,
    },
  },
  methods: {
    dynamicTime,
    toggleEditModal() {
      this.showEditModal = !this.showEditModal;
    },
    openComposeConversationModal(toggleFn) {
      toggleFn();
      // Flag to prevent triggering drag n drop,
      // When compose modal is active
      emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, true);
    },
    closeComposeConversationModal() {
      // Flag to enable drag n drop,
      // When compose modal is closed
      emitter.emit(BUS_EVENTS.NEW_CONVERSATION_MODAL, false);
    },
    toggleDeleteModal() {
      this.showDeleteModal = !this.showDeleteModal;
    },
    confirmDeletion() {
      this.deleteContact(this.contact);
      this.closeDelete();
    },
    closeDelete() {
      this.showDeleteModal = false;
      this.showEditModal = false;
    },
    findCountryFlag(countryCode, cityAndCountry) {
      try {
        if (!countryCode) {
          return `${cityAndCountry} 🌎`;
        }

        const code = countryCode?.toLowerCase();
        return `${cityAndCountry} <span class="fi fi-${code} size-3.5"></span>`;
      } catch (error) {
        return '';
      }
    },
    async deleteContact({ id }) {
      try {
        await this.$store.dispatch('contacts/delete', id);
        this.$emit('panelClose');
        useAlert(this.$t('DELETE_CONTACT.API.SUCCESS_MESSAGE'));

        if (isAConversationRoute(this.$route.name)) {
          this.$router.push({
            name: getConversationDashboardRoute(this.$route.name),
          });
        } else if (isAInboxViewRoute(this.$route.name)) {
          this.$router.push({
            name: 'inbox_view',
          });
        } else if (this.$route.name !== 'contacts_dashboard') {
          this.$router.push({
            name: 'contacts_dashboard',
          });
        }
      } catch (error) {
        useAlert(
          error.message
            ? error.message
            : this.$t('DELETE_CONTACT.API.ERROR_MESSAGE')
        );
      }
    },
    closeMergeModal() {
      this.showMergeModal = false;
    },
    openMergeModal() {
      this.showMergeModal = true;
    },
  },
};
</script>

<template>
  <div class="relative items-center w-full p-4 ">
    <div class="flex flex-col w-full gap-2 text-left rtl:text-right bg-actions-cv">
      <div class="flex flex-row justify-between">
        <Avatar
          v-if="showAvatar"
          :src="contact.thumbnail"
          :name="contact.name"
          :status="contact.availability_status"
          :size="39"
          hide-offline-status
          rounded-full
        />
      </div>

      <div class="flex flex-col items-start gap-1.5 min-w-0 w-full">
        <div v-if="showAvatar" class="flex items-center w-full min-w-0 gap-3">
          <h3
            class="flex-shrink max-w-full min-w-0 my-0 text-base capitalize break-words text-n-slate-12 text-name-contact"
          >
            {{ contact.name }}
          </h3>
          <div class="flex flex-row items-center gap-2">
            <span
              v-if="contact.created_at"
              v-tooltip.left="
                `${$t('CONTACT_PANEL.CREATED_AT_LABEL')} ${dynamicTime(
                  contact.created_at
                )}`
              "
              class="i-lucide-info mt-0.5 mb-0.5 info-created-profile-contactinforow"
            />
            <a
              :href="contactProfileLink"
              target="_blank"
              rel="noopener nofollow noreferrer"
              class="leading-3"
            >
              <span class="i-lucide-external-link " />
            </a>
          </div>
        </div>

        <p
          v-if="additionalAttributes.description"
          class="break-words mb-0.5 text-description-contact"
        >
          {{ additionalAttributes.description }}
        </p>
        <div class="flex flex-col items-start w-full gap-2">
          <ContactInfoRow
            :href="contact.email ? `mailto:${contact.email}` : ''"
            :value="contact.email"
            icon="mail"
            emoji="✉️"
            :title="$t('CONTACT_PANEL.EMAIL_ADDRESS')"
            show-copy
          />
          <ContactInfoRow
            :value="contact.phone_number"
            icon="call"
            emoji="📞"
            :title="$t('CONTACT_PANEL.PHONE_NUMBER')"
            show-copy
            click-to-dial
            :contact-id="contact.id"
            :contact-name="contact.name"
            :conversation-id="
              $route.params.conversationId
                ? Number($route.params.conversationId)
                : null
            "
          />
          <ContactInfoRow
            v-if="contact.identifier"
            :value="contact.identifier"
            icon="contact-identify"
            emoji="🪪"
            :title="$t('CONTACT_PANEL.IDENTIFIER')"
          />
          <ContactInfoRow
            :value="additionalAttributes.company_name"
            icon="building-bank"
            emoji="🏢"
            :title="$t('CONTACT_PANEL.COMPANY')"
          />
          <ContactInfoRow
            v-if="location || additionalAttributes.location"
            :value="location || additionalAttributes.location"
            icon="map"
            emoji="🌍"
            :title="$t('CONTACT_PANEL.LOCATION')"
          />
          <ContactInfoRow
            v-if="whatsappUsername"
            :value="whatsappUsername"
            icon="contact-identify"
            emoji="💬"
            :title="$t('CONTACT_PANEL.WHATSAPP_USERNAME')"
            show-copy
          />
          <ContactInfoRow
            v-if="whatsappBsuid"
            :value="whatsappBsuid"
            icon="contact-identify"
            emoji="🆔"
            :title="$t('CONTACT_PANEL.WHATSAPP_BSUID')"
            show-copy
          />
          <SocialIcons :social-profiles="socialProfiles" />
        </div>
      </div>
      <div class="flex items-center justify-between w-full mt-2">
        <div class="bg-actions-cv flex items-center gap-2">
          <ComposeConversation
            :contact-id="String(contact.id)"
            is-modal
            @close="closeComposeConversationModal"
          >
            <template #trigger="{ toggle }">
              <NextButton
                v-tooltip.top-end="$t('CONTACT_PANEL.NEW_MESSAGE')"
                solid
                sm
                class="bg-chat-contactinfo"
                @click="openComposeConversationModal(toggle)"
              >
                <template #icon>
                  <svg
                    class="icon-chat-contactinfo"
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="15"
                    viewBox="0 0 16 15"
                    fill="none"
                  >
                    <path
                      d="M13.9829 1H7.49147C6.80832 1 6.255 1.55332 6.255 2.23647V7.49146C6.255 8.17461 6.80832 8.72793 7.49147 8.72793H10.9845L13.1421 10.9598C13.3338 11.1607 13.6738 11.0216 13.6738 10.7465V8.72793H13.9829C14.6661 8.72793 15.2194 8.17461 15.2194 7.49146V2.23647C15.2194 1.55332 14.6661 1 13.9829 1Z"
                      stroke="white"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M9.34617 9.34622V9.96445C9.34617 10.6476 8.79285 11.2009 8.1097 11.2009H5.23491L3.07727 13.4327C2.88562 13.6337 2.54559 13.4946 2.54559 13.2195V11.2009H2.23647C1.55332 11.2009 1 10.6476 1 9.96445V5.01857C1 4.33542 1.55332 3.7821 2.23647 3.7821H5.63676"
                      stroke="white"
                      stroke-linejoin="round"
                    />
                    <path d="M8.10971 3.16382H13.3647" stroke="white" stroke-linejoin="round" />
                    <path d="M8.10971 4.70935H13.3647" stroke="white" stroke-linejoin="round" />
                    <path d="M8.10971 6.255H10.5826" stroke="white" stroke-linejoin="round" />
                  </svg>
                </template>
              </NextButton>
            </template>
          </ComposeConversation>
          <VoiceCallButton
            v-if="!canUseNativeBrowserPhone"
            :phone="contact.phone_number"
            :contact-id="contact.id"
            icon="i-ri-phone-fill"
            size="sm"
            :tooltip-label="$t('CONTACT_PANEL.CALL')"
            solid
            class="bg-change-contactinfo"
          />
          <NextButton
            v-if="canEditContactProfile"
            v-tooltip.top-end="$t('EDIT_CONTACT.BUTTON_LABEL')"
            solid
            sm
            class="bg-change-contactinfo"
            @click="toggleEditModal"
          >
            <template #icon>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="15"
                viewBox="0 0 16 15"
                fill="none"
              >
                <path
                  d="M11.8946 6.47368C13.4063 6.47368 14.6315 5.24852 14.6315 3.73684C14.6315 2.22516 13.4063 1 11.8946 1H3.68411C2.17243 1 0.947266 2.22516 0.947266 3.73684C0.947266 5.24852 2.17243 6.47368 3.68411 6.47368H11.8946Z"
                  stroke="white"
                  stroke-miterlimit="10"
                  stroke-linecap="round"
                />
                <path
                  d="M3.68402 1C5.1957 1 6.42086 2.22516 6.42086 3.73684C6.42086 5.24852 5.1957 6.47368 3.68402 6.47368"
                  stroke="white"
                  stroke-miterlimit="10"
                  stroke-linecap="round"
                />
                <path
                  d="M3.68411 14.0002C2.17243 14.0002 0.947266 12.775 0.947266 11.2633C0.947266 9.75165 2.17243 8.52649 3.68411 8.52649H11.8946C13.4063 8.52649 14.6315 9.75165 14.6315 11.2633C14.6315 12.775 13.4063 14.0002 11.8946 14.0002H3.68411Z"
                  stroke="white"
                  stroke-miterlimit="10"
                  stroke-linecap="round"
                />
                <path
                  d="M11.8943 8.52649C10.3827 8.52649 9.1575 9.75165 9.1575 11.2633C9.1575 12.775 10.3827 14.0002 11.8943 14.0002"
                  stroke="white"
                  stroke-miterlimit="10"
                  stroke-linecap="round"
                />
              </svg>
            </template>
          </NextButton>
          <NextButton
            v-if="isAdmin"
            v-tooltip.top-end="$t('CONTACT_PANEL.MERGE_CONTACT')"
            solid
            sm
            class="bg-merge-contactinfo"
            :disabled="uiFlags.isMerging"
            @click="openMergeModal"
          >
            <template #icon>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M14.1667 3C14.2875 3.00001 14.4042 3.04376 14.4953 3.12318C14.5864 3.20259 14.6456 3.3123 14.662 3.432L14.6667 3.5V12.1693C14.6666 12.296 14.6185 12.418 14.532 12.5105C14.4455 12.6031 14.3271 12.6594 14.2008 12.668C14.0744 12.6767 13.9494 12.637 13.8511 12.557C13.7529 12.4771 13.6886 12.3628 13.6713 12.2373L13.6667 12.1693V3.5C13.6667 3.36739 13.7194 3.24021 13.8131 3.14645C13.9069 3.05268 14.0341 3 14.1667 3ZM8.43134 4.20267L8.47934 4.14667C8.56404 4.06181 8.67641 4.01022 8.79597 4.00129C8.91552 3.99235 9.03431 4.02668 9.13068 4.098L9.18668 4.14667L12.518 7.478C12.6027 7.56266 12.6542 7.67489 12.6631 7.7943C12.672 7.91371 12.6378 8.03235 12.5667 8.12867L12.518 8.18533L9.18668 11.5207C9.09734 11.6093 8.97787 11.661 8.8521 11.6654C8.72634 11.6699 8.60352 11.6267 8.50817 11.5446C8.41283 11.4624 8.35196 11.3474 8.33772 11.2223C8.32348 11.0973 8.35691 10.9715 8.43134 10.87L8.47934 10.814L10.9533 8.336H1.83334C1.71262 8.33602 1.59596 8.29235 1.50492 8.21307C1.41388 8.13379 1.35459 8.02425 1.33801 7.90467L1.33334 7.83667C1.33335 7.71584 1.37711 7.59911 1.45652 7.50805C1.53594 7.41699 1.64564 7.35777 1.76534 7.34133L1.83334 7.33667L10.962 7.336L8.47934 4.85333C8.39476 4.76859 8.3434 4.65631 8.33459 4.53691C8.32579 4.4175 8.36012 4.2989 8.43134 4.20267Z"
                  fill="white"
                />
              </svg>
            </template>
          </NextButton>
          <NextButton
            v-if="canDeleteContactProfile"
            v-tooltip.top-end="$t('DELETE_CONTACT.BUTTON_LABEL')"
            solid
            sm
            class="bg-delete-contactinfo"
            :disabled="uiFlags.isDeleting"
            @click="toggleDeleteModal"
          >
            <template #icon>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="13"
                height="15"
                viewBox="0 0 13 15"
                fill="none"
              >
                <path
                  d="M11.3125 4.0625L10.09 13.1199C10.029 13.6394 9.58902 14.0312 9.06566 14.0312H3.93391C3.41098 14.0312 2.97055 13.6394 2.90953 13.1199L1.6875 4.0625"
                  stroke="white"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M5.15809 11.7986L4.78125 5.4375"
                  stroke="white"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M8.21879 5.4375L7.84195 11.7986"
                  stroke="white"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path d="M1 2.6875H12" stroke="white" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M6.5 1.3125V2.6875" stroke="white" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </template>
          </NextButton>
          <NextButton
            v-if="showScheduleAction"
            v-tooltip.top-end="$t('CONVERSATION_SIDEBAR.SCHEDULE.CTA')"
            icon="i-lucide-calendar-plus"
            solid
            sm
            class="bg-schedule-contactinfo"
            @click="$emit('openSchedule')"
          />
          <slot name="action-buttons" />
        </div>
      </div>
      <EditContact
        v-if="showEditModal && canEditContactProfile"
        :show="showEditModal"
        :contact="contact"
        :lock-identity-fields="isContactIdentityLockedForCurrentUser"
        @cancel="toggleEditModal"
      />
      <ContactMergeModal
        v-if="showMergeModal"
        :primary-contact="contact"
        :show="showMergeModal"
        @close="closeMergeModal"
      />
    </div>
    <woot-delete-modal
      v-if="showDeleteModal"
      v-model:show="showDeleteModal"
      :on-close="closeDelete"
      :on-confirm="confirmDeletion"
      :title="$t('DELETE_CONTACT.CONFIRM.TITLE')"
      :message="$t('DELETE_CONTACT.CONFIRM.MESSAGE')"
      :message-value="confirmDeleteMessage"
      :confirm-text="$t('DELETE_CONTACT.CONFIRM.YES')"
      :reject-text="$t('DELETE_CONTACT.CONFIRM.NO')"
    />
  </div>
</template>

<style lang="scss" scoped>
.bg-actions-cv {
  background-color: #f5f7f6 !important;
}

.bg-chat-contactinfo {
  width: 25px !important;
  height: 25px !important;
  border-radius: 5px !important;
  background-color: #3cbeb6 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0 !important;
  color: #fff !important;
}

.icon-chat-contactinfo {
  width: 14.219px;
  height: 12.528px;
  flex-shrink: 0;
}

.bg-change-contactinfo {
  width: 25px !important;
  height: 25px !important;
  border-radius: 5px !important;
  background-color: #00aeef !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0 !important;
  color: #fff !important;
}

.bg-merge-contactinfo {
  width: 25px !important;
  height: 25px !important;
  border-radius: 5px !important;
  background-color: #2d398e !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0 !important;
  color: #fff !important;
}

.bg-delete-contactinfo {
  width: 25px !important;
  height: 25px !important;
  border-radius: 5px !important;
  background-color: #da1c5c !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0 !important;
  color: #fff !important;
}

.bg-schedule-contactinfo {
  width: 25px !important;
  height: 25px !important;
  border-radius: 5px !important;
  background-color: #7c3aed !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  padding: 0 !important;
  color: #fff !important;
}

.bg-actions-cv {
  background-color: #f5f7f6;
}

.text-name-contact {
  font-family: 'Proxima Nova';
}

.text-description-contact {
  font-family: 'Proxima Nova';
}

.info-created-profile-contactinforow {
  margin-left: 30px;
}

</style>
